import React, { useEffect, useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionTitle } from '@/components/common/SectionTitle';
import { Card, CardContent } from '@/components/ui/card';
import { resultsData, yearWiseResults } from '@/data/resultsData';
import { Award, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { fetchResults } from '@/store/slices/resultsSlice';

const ResultsPage = () => {
  const [activeFilter, setActiveFilter] = useState('All');

  const dispatch = useDispatch();
  const { data, fetchLoading, error } = useSelector((state) => state.results);

  useEffect(() => {
    dispatch(fetchResults());
  }, [dispatch]);

  const apiResults = data?.data || [];

  const getAvatar = (name) =>
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Student')}&background=random&size=200`;

  /* ======================
     API SHAPE → UI SHAPE (dynamic)
  ====================== */
  const allResults = apiResults.map((r) => ({
    image: r.student_image || getAvatar(r.student_name),
    name: r.student_name,
    result: r.percentage ? `${r.percentage}%` : '',
    exam: r.exam_name,
    type: r.exam_name
  }));

  /* Top Achievers = 90%+ wale students (dynamic) */
  const topAchievers = apiResults
    .filter((r) => parseFloat(r.percentage || 0) >= 90)
    .map((r) => ({
      image: r.student_image || getAvatar(r.student_name),
      name: r.student_name,
      result: r.percentage ? `${r.percentage}%` : '',
      exam: r.exam_name
    }));

  /* DYNAMIC FILTER TABS — API exam_name se banenge */
  const examTypes = [...new Set(apiResults.map((r) => r.exam_name).filter(Boolean))];
  const filterTabs = ['All', ...examTypes];

  const filteredResults =
    activeFilter === 'All'
      ? allResults
      : allResults.filter((item) => item.type === activeFilter);

  return (
    <div>
      <PageHeader
        title="Our Results & Achievements"
        subtitle="Consistent excellence in JEE, and NEET examinations"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Results' }
        ]}
      />
      {/* TOP HIGHLIGHTED RESULTS (dynamic) */}
      <section className="section-padding bg-primary/5">
        <div className="container-custom">
          <SectionTitle
            title="Top Achievers"
            description="Our best results across all programs"
            className="mb-12"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {topAchievers.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="h-full overflow-hidden"
              >
                <Card className="relative h-full border-2 hover:border-primary transition-all hover:shadow-xl">

                  {item.result && (
                    <div className="absolute top-4 -left-9 w-1/2 md:w-2/3 xl:w-1/2 -rotate-45 text-center
                      bg-gradient-to-br from-primary via-primary-dark to-secondary
                      text-white text-sm font-bold py-2 shadow-md">
                      {item.result}
                    </div>
                  )}

                  <CardContent className="p-5 flex flex-col items-center text-center">
                    <div className="relative mb-4 mt-2">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="h-36 w-36 rounded-full object-cover border-4 border-primary m-auto"
                      />
                      <div className="absolute -bottom-2 -right-2 bg-primary text-white rounded-full p-1">
                        <Award className="h-4 w-4" />
                      </div>
                    </div>

                    <h4 className="text-lg font-bold text-foreground">
                      {item.name}
                    </h4>
                    <div className="mt-3 inline-block px-4 py-1.5 bg-secondary/10
                      text-secondary rounded-full text-xs font-semibold">
                      {item.exam}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* All Results (dynamic) */}
      <section className="section-padding bg-surface-alt">
        <div className="container-custom">
          <SectionTitle
            title="All Results"
            description="Explore achievements across all categories"
            className="mb-12"
          />

          {/* FILTER BUTTONS (dynamic) */}
          <div className="flex flex-wrap justify-center gap-3 mb-14">
            {filterTabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveFilter(tab)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all
                  ${activeFilter === tab
                    ? 'bg-primary text-white'
                    : 'bg-muted text-foreground hover:bg-primary/10'
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* RESULTS GRID (dynamic) */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {filteredResults.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="h-full overflow-hidden"
              >
                <Card className="relative h-full border-2 hover:border-primary transition-all hover:shadow-xl">

                  {item.result && (
                    <div className="absolute top-4 -left-9 w-1/2 md:w-2/3 xl:w-1/2 -rotate-45 text-center
                      bg-gradient-to-br from-primary via-primary-dark to-secondary
                      text-white text-sm font-bold py-2 shadow-md">
                      {item.result}
                    </div>
                  )}

                  <CardContent className="p-5 flex flex-col items-center text-center">
                    <div className="relative mb-4 mt-2">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="h-36 w-36 rounded-full object-cover border-4 border-primary m-auto"
                      />
                      <div className="absolute -bottom-2 -right-2 bg-primary text-white rounded-full p-1">
                        <Award className="h-4 w-4" />
                      </div>
                    </div>

                    <h4 className="text-lg font-bold text-foreground">
                      {item.name}
                    </h4>
                    <div className="mt-3 inline-block px-4 py-1.5 bg-secondary/10
                      text-secondary rounded-full text-xs font-semibold">
                      {item.exam}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics (STATIC — resultsData se) */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionTitle
            title="Overall Performance Statistics"
            description="Our consistent track record across all programs"
            className="mb-16"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {resultsData.statistics.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="text-center border-2 hover:border-primary transition-all duration-300 hover:shadow-lg">
                  <CardContent className="p-8">
                    <div className="text-4xl mb-5">{stat.icon}</div>
                    <div className="text-3xl font-heading font-bold text-primary mb-2">
                      {stat.value}
                    </div>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Year-wise Performance Chart (STATIC — yearWiseResults se) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card>
              <CardContent className="p-8">
                <div className="flex items-center space-x-3 mb-8">
                  <TrendingUp className="h-6 w-6 text-primary" />
                  <h3 className="text-2xl font-heading font-bold text-foreground">
                    Year-wise Performance Trend
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={yearWiseResults}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="jeeAdvanced" name="JEE Advanced" fill="hsl(var(--primary))" />
                    <Bar dataKey="jeeMain" name="JEE Main" fill="hsl(var(--secondary))" />
                    <Bar dataKey="boards" name="Boards" fill="hsl(var(--accent-gold))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ResultsPage;